import {
    Credentials,
    DatabaseClusterEngine,
    DatabaseSecret,
    IParameterGroup,
    ParameterGroup,
    ServerlessCluster,
    ServerlessScalingOptions,
    ServerlessClusterProps,
    IClusterEngine,
} from 'aws-cdk-lib/aws-rds'
import {Optional} from '@othree.io/optional'
import {IVpc, SelectedSubnets} from 'aws-cdk-lib/aws-ec2'
import {Stack} from 'aws-cdk-lib'

export type ServerlessRdsConfiguration = Readonly<{
    username: string
    defaultDatabaseName: string
    scaling: ServerlessScalingOptions
}>

export type ServerlessRdsInput = Readonly<{
    clusterName: string
    selectedSubnets: SelectedSubnets
    vpc: IVpc
    credentials: Credentials
}>

export type EngineConfiguration = Readonly<{
    engine: IClusterEngine
    parameterGroup: IParameterGroup
}>

/** Builds a serverless RDS configuration from scaling options, database name, and username. */
export const withServerlessRdsConfiguration = (deps: {
    serverlessScalingOptions: ServerlessScalingOptions
}) =>
    (databaseName: string, username: string): ServerlessRdsConfiguration => {
        return {
            username: username,
            defaultDatabaseName: databaseName,
            scaling: deps.serverlessScalingOptions,
        }
    }


/** Returns an engine configuration for Aurora PostgreSQL 10. */
export const withAuroraPostgresSql10EngineConfiguration = (deps: {
    stack: Stack
    parameterGroupId: string
}): EngineConfiguration => {
    return {
        engine: DatabaseClusterEngine.AURORA_POSTGRESQL,
        parameterGroup: ParameterGroup.fromParameterGroupName(deps.stack, deps.parameterGroupId, 'default.aurora-postgresql10'),
    }
}

/** Creates a Secrets Manager secret containing database credentials. */
export const withDatabaseCredentials = (deps: { stack: Stack }) =>
    (name: string, username: string): DatabaseSecret => {
        return new DatabaseSecret(deps.stack, name, {
            secretName: name,
            username: username,
        })
    }

/** Creates a Serverless Aurora RDS cluster with the configured engine and scaling options. */
export const withServerlessRds = (deps: {
    stack: Stack
    serverlessRdsConfiguration: ServerlessRdsConfiguration
    engineConfiguration: EngineConfiguration
}) =>
    (input: ServerlessRdsInput, configuration?: Partial<ServerlessClusterProps>): ServerlessCluster => {
        const dbServer = new ServerlessCluster(deps.stack, input.clusterName, {
            vpcSubnets: input.selectedSubnets,
            credentials: input.credentials,
            parameterGroup: deps.engineConfiguration.parameterGroup,
            vpc: input.vpc,
            defaultDatabaseName: deps.serverlessRdsConfiguration.defaultDatabaseName,
            engine: deps.engineConfiguration.engine,
            scaling: deps.serverlessRdsConfiguration.scaling,
            ...Optional(configuration).orElse({}),
        })

        return dbServer
    }
